import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Clock, Loader2, MapPin, MessageSquare, Star, Tent, TrendingUp, User2, Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth";

// Owner Home. Mirrors the mobile app Owner dashboard:
//   • "My Campsites" — the campsites the owner (or staff) manages
//   • "Recent Reviews" feed across all their sites
export default function OwnerHome() {
  const { api, user } = useAuth();
  const [campsites, setCampsites] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // Kick off both loads in parallel; each has its own error to avoid one
    // failure hiding the other's data.
    Promise.allSettled([
      api.get("/campsites/mine"),
      api.get("/owner/reviews/recent", { params: { limit: 8 } }),
    ]).then(([csRes, revRes]) => {
      if (cancelled) return;
      if (csRes.status === "fulfilled") setCampsites(csRes.value.data || []);
      else setError((prev) => prev || csRes.reason?.response?.data?.detail || "Could not load your campsites.");
      if (revRes.status === "fulfilled") setReviews(revRes.value.data?.items || []);
      else setReviews([]); // reviews failing is non-fatal — show empty state
    });
    return () => { cancelled = true; };
  }, [api]);

  const firstName = (user?.display_name || user?.name || "friend").split(" ")[0];

  return (
    <div className="flex flex-col gap-6" data-testid="owner-home-page">
      <header>
        <div className="text-xs font-extrabold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>
          Owner Dashboard
        </div>
        <h1 className="text-2xl md:text-3xl font-extrabold mt-1" style={{ color: "var(--on-surface)" }} data-testid="owner-greeting">
          Welcome back, {firstName}
        </h1>
        <p className="text-sm mt-1" style={{ color: "var(--on-surface-muted)" }}>
          Manage your campsites, watch your reviews, and see who checked in this week.
        </p>
      </header>

      {error && <div className="card-elev p-4" style={{ color: "var(--premium)" }}>{error}</div>}

      {/* Owner quick-links */}
      <section className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="owner-quick-links">
        <Link to="/owner/analytics" className="card-elev p-4 flex items-center gap-3 hover:-translate-y-0.5 transition"
              data-testid="owner-link-analytics">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center"
               style={{ background: "color-mix(in srgb, var(--brand) 12%, transparent)", color: "var(--brand)" }}>
            <TrendingUp className="w-4 h-4" />
          </div>
          <div className="text-sm font-extrabold" style={{ color: "var(--on-surface)" }}>Analytics</div>
        </Link>
        <Link to="/owner/events" className="card-elev p-4 flex items-center gap-3 hover:-translate-y-0.5 transition"
              data-testid="owner-link-events">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center"
               style={{ background: "color-mix(in srgb, var(--premium) 15%, transparent)", color: "var(--premium)" }}>
            <Sparkles className="w-4 h-4" />
          </div>
          <div className="text-sm font-extrabold" style={{ color: "var(--on-surface)" }}>Events</div>
        </Link>
        <Link to="/leaderboards" className="card-elev p-4 flex items-center gap-3 hover:-translate-y-0.5 transition"
              data-testid="owner-link-leaderboards">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center"
               style={{ background: "color-mix(in srgb, var(--brand) 12%, transparent)", color: "var(--brand)" }}>
            <Star className="w-4 h-4" />
          </div>
          <div className="text-sm font-extrabold" style={{ color: "var(--on-surface)" }}>Standings</div>
        </Link>
      </section>

      {/* My campsites */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-extrabold" style={{ color: "var(--on-surface)" }}>
            My campsites {campsites && ` · ${campsites.length}`}
          </h2>
        </div>

        {campsites == null && (
          <div className="card-elev p-6 flex items-center gap-3" style={{ color: "var(--on-surface-muted)" }}>
            <Loader2 className="w-4 h-4 animate-spin" /> Loading your campsites…
          </div>
        )}
        {campsites && campsites.length === 0 && (
          <div className="card-elev p-6 text-sm" style={{ color: "var(--on-surface-muted)" }} data-testid="owner-campsites-empty">
            You haven't added a campsite yet. Please add one from the mobile app for now — full web management is coming soon.
          </div>
        )}
        {campsites && campsites.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="owner-campsite-grid">
            {campsites.map((c) => <OwnerCampsiteCard key={c.campsite_id} c={c} />)}
          </div>
        )}
      </section>

      {/* Recent reviews */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-extrabold" style={{ color: "var(--on-surface)" }}>Recent reviews</h2>
        </div>
        {reviews == null && (
          <div className="card-elev p-6 flex items-center gap-3" style={{ color: "var(--on-surface-muted)" }}>
            <Loader2 className="w-4 h-4 animate-spin" /> Loading reviews…
          </div>
        )}
        {reviews && reviews.length === 0 && (
          <div className="card-elev p-6 text-sm" style={{ color: "var(--on-surface-muted)" }} data-testid="owner-reviews-empty">
            No written reviews yet. When your campers leave feedback it will appear here.
          </div>
        )}
        {reviews && reviews.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4" data-testid="owner-review-list">
            {reviews.map((r) => <ReviewRow key={r.checkin_id} r={r} />)}
          </div>
        )}
      </section>
    </div>
  );
}

function OwnerCampsiteCard({ c }) {
  const status = c.verification_status || "unverified";
  const statusColor = status === "verified" ? "var(--brand)" : status === "pending" ? "var(--premium)" : "var(--on-surface-muted)";
  return (
    <Link to={`/campsites/${c.campsite_id}`} className="card-elev overflow-hidden block hover:-translate-y-0.5 transition"
          data-testid={`owner-campsite-${c.campsite_id}`}>
      <div className="aspect-[16/10] bg-black/10 overflow-hidden">
        {c.photo ? (
          <img src={c.photo} alt={c.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ color: "var(--on-surface-muted)" }}>
            <Tent className="w-8 h-8 opacity-40" />
          </div>
        )}
      </div>
      <div className="p-4 flex flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-extrabold text-base leading-tight" style={{ color: "var(--on-surface)" }}>{c.name}</h3>
          <span className="shrink-0 text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full"
                style={{ background: "color-mix(in srgb, currentColor 15%, transparent)", color: statusColor }}>
            {status}
          </span>
        </div>
        <div className="flex items-center gap-1 text-xs" style={{ color: "var(--on-surface-muted)" }}>
          <MapPin className="w-3.5 h-3.5" /> {c.state || c.location || "Malaysia"}
        </div>
        <div className="flex items-center justify-between text-xs mt-1">
          <span className="inline-flex items-center gap-1 font-bold" style={{ color: "var(--brand)" }}>
            Lvl {c.level ?? 1} · {c.title || "Rookie Site"}
          </span>
          <span className="inline-flex items-center gap-1 font-bold" style={{ color: "var(--premium)" }}>
            {Number(c.xp ?? 0).toLocaleString()} XP
          </span>
        </div>
      </div>
    </Link>
  );
}

function ReviewRow({ r }) {
  const date = (r.review_body_updated_at || r.created_at || r.checkin_date || "").slice(0, 10);
  return (
    <div className="card-elev p-4 flex flex-col gap-2" data-testid={`owner-review-${r.checkin_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {r.camper_avatar ? (
            <img src={r.camper_avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-extrabold"
                 style={{ background: "var(--brand)", color: "white" }}>
              {(r.camper_display_name?.[0] || "C").toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-sm font-bold truncate" style={{ color: "var(--on-surface)" }}>
              {r.camper_display_name || "Camper"}
            </div>
            <div className="text-[11px]" style={{ color: "var(--on-surface-muted)" }}>
              {r.campsite_name || "Your campsite"} · {date}
            </div>
          </div>
        </div>
        {r.rating != null && (
          <div className="shrink-0 inline-flex items-center gap-1 text-xs font-extrabold" style={{ color: "var(--premium)" }}>
            <Star className="w-3.5 h-3.5" fill="currentColor" /> {Number(r.rating).toFixed(1)}
          </div>
        )}
      </div>
      {r.review_body && (
        <p className="text-sm leading-relaxed" style={{ color: "var(--on-surface)" }}>
          {r.review_body}
        </p>
      )}
    </div>
  );
}
